import { styled } from '@mui/material'
import { PageArea, PageProps } from '..'
import { useProjectPrivateContext } from '../../context/projectContext'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { VersionReportHeader } from './components/header'
import { VersionReportPage } from './components/pages/facade'
import { useCurrentVersion } from './hooks/currentReport'
import { useEditingState } from './hooks/editingState'
import { useEvaluate } from './hooks/evaluate'
import { useDiscussionPanel } from './hooks/useDiscussionPanel'
import { useVersionSelectMenu } from './hooks/useVersionSelectMenu'
import { DiscussionPanel } from './components/discussion-panel/index'
import { VersionSelectMenu } from './components/version-select-menu'
import { Button } from '../../components/buttons'
import Loading, {
  useLoading,
} from '../../components/process-state-notifications/Loading'
import { useEffect } from 'react'
import { intl } from '../../../i18n'

type ProjectVersionReportProps = PageProps
export const ProjectVersionReport = projectPrivate(
  ({ uuid }: ProjectVersionReportProps) => {
    const { projectUuid } = useProjectPrivateContext()
    const { isLoading, showLoading, hideLoading } = useLoading()
    const {
      currentPage,
      currentVersion,
      updateVersionUuid,
      updateCurrentVersion,
      updatePageContent,
      storedVersionUuid,
      initialized,
      ...others
    } = useCurrentVersion(uuid, projectUuid)

    const { editing, ...editingHandlers } = useEditingState(
      currentVersion,
      currentPage,
      updateCurrentVersion
    )
    const { evaluate } = useEvaluate(
      currentVersion,
      currentPage,
      updatePageContent
    )
    const discussionPanel = useDiscussionPanel()
    const {
      isOpen: isVersionSelectMenuOpen,
      toggle: toggleVersionSelectMenu,
      open: openVersionSelectMenu,
      close: closeVersionSelectMenu,
      selectReport,
      handleAfterCreateReport,
    } = useVersionSelectMenu(updateVersionUuid)

    useEffect(() => {
      if (initialized && !storedVersionUuid) {
        openVersionSelectMenu()
      }
    }, [initialized, storedVersionUuid, openVersionSelectMenu])

    if (!initialized) {
      return (
        <PageArea>
          <Loading isLoading={true} />
        </PageArea>
      )
    }

    return (
      <PageArea>
        <Root>
          <VersionReportHeader
            currentPage={currentPage}
            currentVersion={currentVersion}
            {...others}
            editing={editing}
            {...editingHandlers}
            evaluate={evaluate}
            discussionPanel={discussionPanel}
            isVersionSelectMenuOpen={isVersionSelectMenuOpen}
            onReportMenuToggle={toggleVersionSelectMenu}
            showLoading={showLoading}
            hideLoading={hideLoading}
          />
          <MainContent>
            <VersionSelectMenu
              projectUuid={projectUuid}
              open={isVersionSelectMenuOpen}
              onClose={closeVersionSelectMenu}
              onReportSelect={selectReport}
              onAfterCreateReport={handleAfterCreateReport}
            />
            <PageContentArea isDiscussionOpen={discussionPanel.isOpen}>
              {currentVersion ? (
                <>
                  <VersionReportPage
                    snapshotUuid={currentVersion?.snapshotUuid}
                    page={currentPage}
                    editing={editing}
                    updatePageContent={updatePageContent}
                    showLoading={showLoading}
                    hideLoading={hideLoading}
                    {...others}
                  />
                </>
              ) : (
                <TemplateSelectionContainer>
                  <TemplateSelectionContent>
                    <TemplateSelectionButtonContainer>
                      <Button onClick={openVersionSelectMenu}>
                        {intl.formatMessage({
                          id: 'versionReport.selectReport',
                        })}
                      </Button>
                    </TemplateSelectionButtonContainer>
                  </TemplateSelectionContent>
                </TemplateSelectionContainer>
              )}
            </PageContentArea>
            {currentPage && currentVersion && (
              <DiscussionPanel
                versionPageUuid={currentPage.uuid}
                projectUuid={projectUuid}
                isOpen={discussionPanel.isOpen}
                onClose={discussionPanel.close}
              />
            )}
          </MainContent>
          <Loading isLoading={isLoading} />
        </Root>
      </PageArea>
    )
  }
)
const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  height: '100%',
})

const MainContent = styled('div')({
  display: 'flex',
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
})

const PageContentArea = styled('div')<{ isDiscussionOpen: boolean }>(
  ({ isDiscussionOpen }) => ({
    flex: 1,
    overflow: 'auto',
    ...(isDiscussionOpen && {
      paddingRight: '16px',
    }),
    transition: 'all 0.3s ease',
  })
)

const TemplateSelectionContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
})

const TemplateSelectionContent = styled('div')({
  textAlign: 'center',
  padding: '40px',
  maxWidth: '400px',
})

const TemplateSelectionButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
})
